import { useEffect } from 'react';
import { getAppConfig } from '@/config/app-config';
import { getHostName } from '@/tools/office-tools-registry';
import { notifyDatadogReady } from '@/services/datadog';

/**
 * Initializes Datadog RUM when credentials are configured.
 * Mirrors hai_assistants/ui/providers/datadog-rum-provider.tsx.
 *
 * Required env vars:
 *   VITE_DATADOG_RUM_APPLICATION_ID
 *   VITE_DATADOG_RUM_CLIENT_TOKEN
 *
 * Optional:
 *   VITE_DATADOG_RUM_SITE  (default: datadoghq.com)
 *   VITE_DATADOG_RUM_ENV   (default: local)
 */
export function DatadogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const config = getAppConfig();
    const { datadogRumApplicationId, datadogRumClientToken, datadogRumSite, datadogRumEnv } = config;

    if (!datadogRumApplicationId || !datadogRumClientToken) {
      console.warn('[DatadogRUM] Skipping init — missing credentials');
      return;
    }

    console.log('[DatadogRUM] Initializing with appId:', datadogRumApplicationId.substring(0, 8) + '...');

    import('@datadog/browser-rum')
      .then(({ datadogRum }) => {
        if (datadogRum.getInitConfiguration()) {
          console.log('[DatadogRUM] Already initialized, skipping');
          return;
        }

        datadogRum.init({
          applicationId: datadogRumApplicationId,
          clientToken: datadogRumClientToken,
          site: datadogRumSite,
          service: 'hai-office-addin-ui',
          env: datadogRumEnv,
          version: '1.0.0',
          sessionSampleRate: 100,
          sessionReplaySampleRate: 50,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
          // Office Add-in runs in a sandboxed iframe where cookies are blocked.
          // These settings allow Datadog to fall back to localStorage for session tracking.
          allowFallbackToLocalStorage: true,
          usePartitionedCrossSiteSessionCookie: true,
          allowedTracingUrls: [
            { match: /\/api\//, propagatorTypes: ['datadog', 'tracecontext'] },
          ],
          beforeSend: (event) => {
            // Tag all events with the Office host for filtering
            event.context = {
              ...event.context,
              platform: 'office-addin',
              office_host: getHostName(),
            };

            if (event.type === 'error' && event.error) {
              event.context = {
                ...event.context,
                office_host: getHostName(),
              };
            }

            if (event.type === 'resource' && event.resource) {
              const status = event.resource.status_code;
              if (status && status >= 400) {
                event.context = {
                  ...event.context,
                  is_error_response: true,
                };
              }
            }
            return true;
          },
        });

        // Notify the utility module that the SDK is ready so pending
        // addDatadogAction/addDatadogError calls can resolve.
        notifyDatadogReady();
        console.log('[DatadogRUM] Initialized successfully — service: hai-office-addin-ui, env:', datadogRumEnv);
      })
      .catch((err) => {
        console.error('[DatadogRUM] Failed to initialize:', err);
      });
  }, []);

  return <>{children}</>;
}
